// Every coded word nflverse uses, and what we take it to mean.
//
// This is §3.6 of the design made real, and it is the OMOP pattern: a coded
// value is stored three times — the source's word verbatim (in the fact
// payload), the source's word as a mapped row (here), and the standard concept
// every query is eventually written against (`concept.id`).
//
// What it buys today is small and worth having: an extraction reports a coded
// value it has never seen as unmapped vocabulary, so this table is the
// difference between a daily run that says nothing and a daily run that says
// "25 unmapped" until people stop reading it. The day nflverse adds a practice
// status, exactly one line appears.
//
// What it buys later is the reason the tables exist: a second football source
// spells the same things differently, and the mapping is where that is absorbed
// instead of in every query.
//
// NOT YET LOAD-BEARING. The directory projection maps nflverse's words to app
// values directly (packages/sports-football/src/directory.ts) rather than going
// through these concepts, because doing it properly needs the projection to
// know which source each payload came from — a change to its input shape that
// belongs with the read side in Phase 7. This file is correct either way; the
// indirection is what is missing, not the data.

import type { Concept, ConceptMapping } from '../../acquisition/adapter';

const FOOTBALL = 'football';

/**
 * Positions.
 *
 * Two vocabularies arrive under this name and both are mapped: players.csv is
 * fine-grained (25 values — CB, FS, OT), roster_YYYY.csv is coarse (11 — DB,
 * OL). They agree exactly where it matters for fantasy and diverge everywhere
 * it does not, which is why both spellings map to the same concepts rather than
 * one being normalized into the other at ingest.
 */
const POSITION_CONCEPTS: Concept[] = [
  { id: 'position.qb', domain: 'position', sport: FOOTBALL, display: 'Quarterback' },
  { id: 'position.rb', domain: 'position', sport: FOOTBALL, display: 'Running back' },
  { id: 'position.wr', domain: 'position', sport: FOOTBALL, display: 'Wide receiver' },
  { id: 'position.te', domain: 'position', sport: FOOTBALL, display: 'Tight end' },
  { id: 'position.ol', domain: 'position', sport: FOOTBALL, display: 'Offensive line' },
  { id: 'position.dl', domain: 'position', sport: FOOTBALL, display: 'Defensive line' },
  { id: 'position.lb', domain: 'position', sport: FOOTBALL, display: 'Linebacker' },
  { id: 'position.db', domain: 'position', sport: FOOTBALL, display: 'Defensive back' },
  { id: 'position.k', domain: 'position', sport: FOOTBALL, display: 'Kicker' },
  { id: 'position.p', domain: 'position', sport: FOOTBALL, display: 'Punter' },
  { id: 'position.ls', domain: 'position', sport: FOOTBALL, display: 'Long snapper' },
];

const POSITIONS: Record<string, string> = {
  QB: 'position.qb',
  RB: 'position.rb',
  FB: 'position.rb',
  HB: 'position.rb',
  WR: 'position.wr',
  TE: 'position.te',
  // Offensive line, both spellings.
  OL: 'position.ol',
  OT: 'position.ol',
  T: 'position.ol',
  G: 'position.ol',
  C: 'position.ol',
  // Defensive line.
  DL: 'position.dl',
  DE: 'position.dl',
  DT: 'position.dl',
  NT: 'position.dl',
  EDGE: 'position.dl',
  INTERIOR_LINE: 'position.dl',
  // Linebackers.
  LB: 'position.lb',
  OLB: 'position.lb',
  ILB: 'position.lb',
  MLB: 'position.lb',
  // Secondary.
  DB: 'position.db',
  CB: 'position.db',
  S: 'position.db',
  SS: 'position.db',
  FS: 'position.db',
  SAF: 'position.db',
  // Specialists.
  K: 'position.k',
  PK: 'position.k',
  P: 'position.p',
  LS: 'position.ls',
};

/** Roster status. The words on roster_YYYY.csv and players.csv. */
const ROSTER_CONCEPTS: Concept[] = [
  { id: 'roster.active', domain: 'roster-status', sport: FOOTBALL, display: 'Active' },
  {
    id: 'roster.practice-squad',
    domain: 'roster-status',
    sport: FOOTBALL,
    display: 'Practice squad',
  },
  { id: 'roster.reserve', domain: 'roster-status', sport: FOOTBALL, display: 'Reserve' },
  { id: 'roster.released', domain: 'roster-status', sport: FOOTBALL, display: 'Released' },
  { id: 'roster.inactive', domain: 'roster-status', sport: FOOTBALL, display: 'Inactive' },
  { id: 'roster.retired', domain: 'roster-status', sport: FOOTBALL, display: 'Retired' },
  { id: 'roster.exempt', domain: 'roster-status', sport: FOOTBALL, display: 'Exempt' },
  { id: 'roster.suspended', domain: 'roster-status', sport: FOOTBALL, display: 'Suspended' },
];

const ROSTER_STATUS: Record<string, string> = {
  ACT: 'roster.active',
  // The practice squad is its own status, not a kind of inactive: a player on
  // it can be elevated on Saturday and score on Sunday.
  DEV: 'roster.practice-squad',
  RES: 'roster.reserve',
  RSN: 'roster.reserve',
  RSR: 'roster.reserve',
  PUP: 'roster.reserve',
  NWT: 'roster.reserve',
  CUT: 'roster.released',
  RLS: 'roster.released',
  INA: 'roster.inactive',
  RET: 'roster.retired',
  EXE: 'roster.exempt',
  SUS: 'roster.suspended',
};

/** The game-day designation on the participation report. */
const REPORT_CONCEPTS: Concept[] = [
  { id: 'injury.out', domain: 'report-status', sport: FOOTBALL, display: 'Out' },
  { id: 'injury.doubtful', domain: 'report-status', sport: FOOTBALL, display: 'Doubtful' },
  { id: 'injury.questionable', domain: 'report-status', sport: FOOTBALL, display: 'Questionable' },
];

const REPORT_STATUS: Record<string, string> = {
  Out: 'injury.out',
  Doubtful: 'injury.doubtful',
  Questionable: 'injury.questionable',
};

/**
 * Practice participation — the half Sleeper had no concept of.
 *
 * Note the spelling nflverse actually publishes: "Did Not Participate In
 * Practice", with a capital I on "In" and none on the others. It is written
 * here exactly as it arrives, because a mapping table that tidies its inputs
 * matches nothing.
 */
const PRACTICE_CONCEPTS: Concept[] = [
  { id: 'practice.full', domain: 'practice-status', sport: FOOTBALL, display: 'Full practice' },
  {
    id: 'practice.limited',
    domain: 'practice-status',
    sport: FOOTBALL,
    display: 'Limited practice',
  },
  { id: 'practice.none', domain: 'practice-status', sport: FOOTBALL, display: 'Did not practice' },
];

const PRACTICE_STATUS: Record<string, string> = {
  'Full Participation in Practice': 'practice.full',
  'Limited Participation in Practice': 'practice.limited',
  'Did Not Participate In Practice': 'practice.none',
};

/** Every concept these mappings point at. Written before the mappings. */
export const NFLVERSE_CONCEPTS: readonly Concept[] = [
  ...POSITION_CONCEPTS,
  ...ROSTER_CONCEPTS,
  ...REPORT_CONCEPTS,
  ...PRACTICE_CONCEPTS,
];

const flatten = (domain: string, table: Record<string, string>): ConceptMapping[] =>
  Object.entries(table).map(([sourceValue, conceptId]) => ({ domain, sourceValue, conceptId }));

export const NFLVERSE_MAPPINGS: readonly ConceptMapping[] = [
  ...flatten('position', POSITIONS),
  ...flatten('roster-status', ROSTER_STATUS),
  ...flatten('report-status', REPORT_STATUS),
  ...flatten('practice-status', PRACTICE_STATUS),
];
